import React, { useMemo } from "react";
import { compareTerms, pickBestTerm } from "../../lib/portfolio/termComparator";
import { useCurrency } from "../../contexts/CurrencyContext";

function statusBadge(status) {
  const base = "px-2 py-1 rounded text-xs font-medium border";
  switch (status) {
    case "OK":
      return `${base} bg-emerald-500/15 text-emerald-400 border-emerald-500/25`;
    case "BLOQUE_DSCR":
      return `${base} bg-red-500/15 text-red-400 border-red-500/25`;
    case "CASHOUT_FAIBLE":
      return `${base} bg-amber-500/15 text-amber-400 border-amber-500/25`;
    case "DONNEES_INCOMPLETES":
      return `${base} bg-white/10 text-white/70 border-white/10`;
    default:
      return `${base} bg-white/10 text-white/70 border-white/10`;
  }
}

export default function ComparateurTermeHypothecaire({ inputs, assumptions, cfg, objective }) {
  const { formatCurrency } = useCurrency();

  const rows = useMemo(() => {
    return compareTerms({ inputs, cfg, assumptions });
  }, [inputs, cfg, assumptions]);

  const best = useMemo(() => pickBestTerm(rows, objective), [rows, objective]);

  return (
    <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm text-muted-foreground">Comparateur de renouvellement</div>
          <div className="text-lg font-semibold">{inputs?.label || "Propriété"}</div>
        </div>

        {best && (
          <div className="text-sm">
            <span className="text-muted-foreground">Recommandation: </span>
            <span className="font-semibold">{best.termYears} ans</span>
            <span className="text-muted-foreground"> — </span>
            <span className="text-foreground/90">
              cash-out {formatCurrency(best.cashOutMax)} / cashflow {formatCurrency(best.cashflowPostRefiAnnual)} / DSCR {best.dscrPostRefi?.toFixed?.(2) ?? "—"}
            </span>
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-[980px] w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="py-2">Terme</th>
              <th>Date</th>
              <th>Solde</th>
              <th>Valeur</th>
              <th>Cash-out max</th>
              <th>Taux stress</th>
              <th>Dette/an</th>
              <th>NOI</th>
              <th>DSCR</th>
              <th>Cashflow/an</th>
              <th>Statut</th>
              <th>Pourquoi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r) => (
              <tr key={r.termYears} className={best?.termYears === r.termYears ? "bg-muted/30" : ""}>
                <td className="py-2 font-medium">{r.termYears} ans</td>
                <td>{r.renewalDateISO}</td>
                <td>{formatCurrency(r.mortgageBalanceAtRenewal)}</td>
                <td>{formatCurrency(r.projectedValueAtRenewal)}</td>
                <td className="font-medium">{formatCurrency(r.cashOutMax)}</td>
                <td>{Number(r.stressTestRate).toFixed(2)}%</td>
                <td>{formatCurrency(r.postRefiAnnualDebtService)}</td>
                <td>{formatCurrency(r.noiAtRenewal)}</td>
                <td>{r.dscrPostRefi?.toFixed?.(2) ?? "—"}</td>
                <td>{formatCurrency(r.cashflowPostRefiAnnual)}</td>
                <td>
                  <span className={statusBadge(r.status)}>{r.status}</span>
                </td>
                <td className="text-muted-foreground">
                  {r.reasons?.length ? r.reasons.join(" • ") : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-muted-foreground">
        Règles conservatrices: LTV max {(cfg.conservative.ltvMaxRefi * 100).toFixed(0)}% • vacance {(cfg.conservative.vacancyRate * 100).toFixed(0)}% • capex {(cfg.conservative.capexReserveRate * 100).toFixed(0)}% • DSCR min {cfg.conservative.dscrMin.toFixed(2)} • stress test max(taux+{cfg.conservative.stressTestBuffer}%, {cfg.conservative.stressTestFloorRate}%)
      </div>
    </div>
  );
}
